/*
 * THE CREW GRID — running tally and a single multi-line add.
 *
 * ★ ONE REQUEST, NOT ONE PER SIZE. /cart/add.js takes an `items` array. Adding eleven sizes as
 *   eleven sequential POSTs is eleven chances to half-fail and leaves the buyer with a cart that
 *   is neither what they typed nor empty. One call either lands or does not.
 *
 * ★ THE PACK RULE IS APPLIED IN THE BOX, NOT AT THE CART. Each cell rounds UP to the product's
 *   multiple on blur, and the tally says what changed. This is the same rule the buyer's Hub
 *   applies (applyMinQty) — the difference is that here they watch it happen.
 *
 * No framework, no build step: this is a theme asset and it has to survive the next theme update.
 */
(function () {
  'use strict';

  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  document.querySelectorAll('[data-crew]').forEach(function (root) {
    var moq = parseInt(root.dataset.moq, 10) || 1;
    var unit = (root.dataset.unit || 'each').toLowerCase();
    var lineNoun = root.dataset.lineNoun || 'size';
    // ★ "each" DOES NOT TAKE AN S. The unit comes from the `sold_as` metafield — Pair, Box,
    // Each — and blind pluralising produced "18 eachs" on the first matrix product tested.
    function units(n) {
      if (unit === 'each' || unit === 'ea') return n === 1 ? 'item' : 'items';
      return n === 1 ? unit : unit + 's';
    }
    var addBtn = root.querySelector('[data-crew-add]');
    var clearBtn = root.querySelector('[data-crew-clear]');
    var tally = root.querySelector('[data-tally]');
    var tallyQty = root.querySelector('[data-total-qty]');
    var tallyMoney = root.querySelector('[data-total-money]');
    var tallyNote = root.querySelector('[data-tally-note]');
    var status = root.querySelector('[data-crew-status]');
    var inputs = Array.prototype.slice.call(root.querySelectorAll('.crew__qty'));
    // ★ WHICH cells were rounded, not HOW MANY. A single counter was reset by the next
    // keystroke anywhere in the grid, so the buyer saw "we rounded one up" flash and vanish
    // while they were still filling the roster — the one moment the sentence has to survive.
    // Membership is cleared per input, when that input is edited.
    var rounded = new Set();
    var busy = false;

    /* ── "why is this greyed out?" (2026-09-23) ──────────────────────────────
       The button is aria-disabled rather than disabled, so an empty grid can ANSWER a click
       instead of showing the browser's red no-entry cursor and nothing else. One sentence, the
       missing control focused. Works for the one-box single-variant case as well as the matrix,
       so the wording names a quantity rather than a size. */
    function setReady(on) { addBtn.setAttribute('aria-disabled', on ? 'false' : 'true'); }

    /* ★ ONLY THE NUDGE SELF-CLEARS. recalc() runs in the add's .finally too, so clearing every
       is-err message there erased the "we could not add those" line the buyer most needs — the
       grid went silent on a failed add. Caught by a real 429 from Shopify during verification.
       This flag keeps the two apart: the nudge is ours to withdraw, a failure is not. */
    var asking = false;

    function askForQty() {
      asking = true;
      status.className = 'crew__status is-err';
      status.textContent = 'Enter a quantity first, then add to cart.';
      var first = inputs[0];
      if (first) {
        first.classList.add('is-asking');
        setTimeout(function () { first.classList.remove('is-asking'); }, 1400);
        if (typeof first.focus === 'function') first.focus();
      }
    }

    function recalc() {
      var qty = 0, cents = 0, lines = 0;
      inputs.forEach(function (i) {
        var n = parseInt(i.value, 10);
        if (!n || n < 0) return;
        qty += n;
        cents += n * (parseInt(i.dataset.price, 10) || 0);
        lines += 1;
      });

      // Row totals only exist in the two-option matrix.
      root.querySelectorAll('tr').forEach(function (tr) {
        var cell = tr.querySelector('[data-rowtotal]');
        if (!cell) return;
        var sum = 0;
        tr.querySelectorAll('.crew__qty').forEach(function (i) {
          sum += parseInt(i.value, 10) || 0;
        });
        cell.textContent = sum;
        cell.classList.toggle('is-on', sum > 0);
      });

      tallyQty.textContent = qty;
      tallyMoney.textContent = money(cents);
      tally.hidden = qty === 0;
      clearBtn.hidden = qty === 0;
      setReady(qty > 0);
      if (qty > 0 && asking) { asking = false; status.className = 'crew__status'; status.textContent = ''; }
      addBtn.textContent = lines > 1
        ? 'Add ' + lines + ' ' + lineNoun + 's to cart'
        : 'Add to cart';

      if (rounded.size > 0 && qty > 0) {
        tallyNote.hidden = false;
        tallyNote.textContent = rounded.size === 1
          // Same wording as the price line and the grid note — one page, one phrasing.
          ? 'One size was rounded up — these are sold in multiples of ' + moq + '.'
          : rounded.size + ' sizes were rounded up — these are sold in multiples of ' + moq + '.';
      } else {
        tallyNote.hidden = true;
      }
    }

    inputs.forEach(function (input) {
      input.addEventListener('input', function () { rounded.delete(input); recalc(); });
      input.addEventListener('blur', function () {
        var n = parseInt(input.value, 10);
        if (!n || n <= 0) { input.value = ''; recalc(); return; }
        if (moq > 1 && n % moq !== 0) {
          input.value = Math.ceil(n / moq) * moq;
          input.classList.add('was-rounded');
          rounded.add(input);
          setTimeout(function () { input.classList.remove('was-rounded'); }, 1200);
        }
        recalc();
      });
    });

    clearBtn.addEventListener('click', function () {
      inputs.forEach(function (i) { i.value = ''; });
      rounded.clear();
      status.textContent = '';
      recalc();
    });

    addBtn.addEventListener('click', function () {
      var items = inputs.reduce(function (acc, i) {
        var n = parseInt(i.value, 10);
        if (n > 0) acc.push({ id: parseInt(i.dataset.variant, 10), quantity: n });
        return acc;
      }, []);
      if (busy) return;
      if (!items.length) { askForQty(); return; }

      busy = true;
      setReady(false);
      var previous = addBtn.textContent;
      addBtn.textContent = 'Adding…';
      status.textContent = '';

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body && res.body.description ? res.body.description : 'Could not add these');
          var total = items.reduce(function (s, i) { return s + i.quantity; }, 0);
          status.className = 'crew__status is-ok';
          // "3 items across 1 size" is a sentence about a grid, said to someone who filled in one
          // box on a product that has no sizes. One line gets the plain version.
          status.textContent = items.length === 1
            ? total + ' ' + units(total) + ' added to your cart.'
            : total + ' ' + units(total) + ' across ' + items.length + ' ' + lineNoun + 's added to your cart.';
          inputs.forEach(function (i) { i.value = ''; });
          rounded.clear();
          recalc();
          // Tell the theme's own cart bubble; it is the header count the buyer looks at.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          fetch(window.Shopify.routes.root + 'cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(function (el) {
                el.textContent = cart.item_count;
              });
              document.querySelectorAll('.cart-count-bubble').forEach(function (el) { el.hidden = false; });
            })
            .catch(function () { /* the count is cosmetic; the cart is already right */ });
        })
        .catch(function (err) {
          status.className = 'crew__status is-err';
          // Say what failed and what to do — never "something went wrong".
          status.textContent = err.message || 'We could not add those just now. Try again, or call us.';
        })
        .finally(function () {
          busy = false;
          addBtn.textContent = previous;
          recalc();   // recomputes the ready state from what is still typed in
        });
    });

    recalc();
  });
})();
